import { DEFAULT_POLICY, WIX_EU_POLICY, TranslateFn } from './types'
import { byHook, render, logger, getLanguage, getStaticsUrl } from './utils'
import { init as initTranslations } from './i18n'

import {
  withRootTemplate,
  createModalTemplate,
  createBannerTemplate,
  MODAL_SAVE_HOOK,
  MODAL_CLOSE_HOOK,
  MODAL_RECOMMENDED_CHECKBOX_HOOK,
  BANNER_ACCEPT_HOOK,
  BANNER_DECLINE_HOOK,
  BANNER_SETTINGS_HOOK,
  MODAL_HOOK,
  LINK_HOOK,
} from './components'

import classes from './components/root/root.css'

export const BANNER_WRAPPER_HOOK = 'banner-wrapper'
export const MODAL_WRAPPER_HOOK = 'modal-wrapper'

const noop = () => null

/**
 * `fontsLoading` and `fontsFallback` are changed in `onFontLoaded`,
 * which is executed as `callback` in ~/utils/font-loader.ts
 * This needed for:
 * * remove `hidden` class name from root, because banner is hidden by default
 * * add `fontFallback` class name, if fonts are not loaded
 */

let fontsLoading = true
let fontsFallback = false

const rootClassName = (fallback: boolean, hidden: boolean) => {
  let className = classes.root
  if (fallback) {
    className += ` ${classes.fontFallback}`
  }
  if (hidden) {
    className += ` ${classes.hidden}`
  }

  return className
}

const renderBanner = (t: TranslateFn, showClose: boolean = true) => {
  const consentPolicyManager = (window as any).consentPolicyManager
  const { defaultPolicy } = consentPolicyManager.getCurrentConsentPolicy()

  if (!defaultPolicy) {
    return
  }

  logger.rendered()

  const root = render(
    withRootTemplate(
      createBannerTemplate(t, showClose),
      BANNER_WRAPPER_HOOK,
      rootClassName(fontsFallback, fontsLoading),
    ),
  )
  document.body.appendChild(root)
  const removeRoot = () => {
    root.parentElement?.removeChild(root)
  }

  root
    .querySelector(byHook(BANNER_ACCEPT_HOOK))
    ?.addEventListener('click', () => {
      logger.acceptButtonClicked()
      consentPolicyManager.setConsentPolicy(DEFAULT_POLICY, noop, noop)
      removeRoot()
    })

  root
    .querySelector(byHook(BANNER_DECLINE_HOOK))
    ?.addEventListener('click', () => {
      logger.dismissClicked()
      removeRoot()
    })

  root.querySelector(byHook(LINK_HOOK))?.addEventListener('click', () => {
    logger.cookieLinkClicked()
  })

  root
    .querySelector(byHook(BANNER_SETTINGS_HOOK))
    ?.addEventListener('click', () => {
      logger.openedPanel()
      const modal = render(
        withRootTemplate(
          createModalTemplate(t),
          MODAL_WRAPPER_HOOK,
          rootClassName(fontsFallback, false),
        ),
      )
      root.appendChild(modal)
      const sections = modal.getElementsByTagName('section')
      if (sections.length > 0) {
        sections[0].focus()
      }

      const closeModalOnEsc = ({ keyCode }: KeyboardEvent) => {
        if (keyCode !== 27) {
          // 27 is `Esc`
          return
        }

        removeModal()
      }

      const removeModal = () => {
        root.removeChild(modal)
        logger.closePanelClicked()
        document.removeEventListener('keyup', closeModalOnEsc)
      }

      document.addEventListener('keyup', closeModalOnEsc, { once: true })

      modal
        .querySelector(byHook(MODAL_SAVE_HOOK))
        ?.addEventListener('click', () => {
          const checkbox = modal.querySelector(
            byHook(MODAL_RECOMMENDED_CHECKBOX_HOOK),
          ) as HTMLInputElement

          const userAcceptedRecommendedCookies = checkbox.checked

          logger.saveButtonClicked(userAcceptedRecommendedCookies)

          consentPolicyManager.setConsentPolicy(
            userAcceptedRecommendedCookies ? DEFAULT_POLICY : WIX_EU_POLICY,
            noop,
            noop,
          )

          removeModal()
          removeRoot()
        })

      modal
        .querySelector(byHook(LINK_HOOK))
        ?.addEventListener('click', logger.cookieLinkClickedModal)

      modal
        .querySelector(byHook(MODAL_RECOMMENDED_CHECKBOX_HOOK))
        ?.addEventListener('click', () => {
          const checkbox = modal.querySelector(
            byHook(MODAL_RECOMMENDED_CHECKBOX_HOOK),
          ) as HTMLInputElement

          const toggle = checkbox.checked
          logger.toggleClicked(toggle)
        })

      modal
        .querySelector(byHook(MODAL_CLOSE_HOOK))
        ?.addEventListener('click', () => {
          removeModal()
        })

      modal
        .querySelector(byHook(MODAL_HOOK))
        ?.addEventListener('click', function (this: HTMLElement, e: Event) {
          if (e.target !== this) {
            return
          }

          removeModal()
        })
    })
}

const onFontLoaded = (fallback = false) => {
  fontsLoading = false
  const root = document.querySelector(byHook(BANNER_WRAPPER_HOOK))
  root?.classList.remove(classes.hidden)

  if (fallback) {
    fontsFallback = true
    root?.classList.add(classes.fontFallback)
  }
}

export const ConsentCookieBanner = (showClose: boolean = true) => {
  initTranslations(getStaticsUrl(), getLanguage(), (translate: TranslateFn) => {
    renderBanner(translate, showClose)
  })
}

ConsentCookieBanner.onFontsLoaded = onFontLoaded
